<template>
  <v-sheet class="pa-4 h-100">
    <v-sheet outlined class="rounded overflow-hidden h-100 d-flex flex-column">
      <div
        class="pa-2 d-flex align-center justify-space-between"
        style="gap: 8px"
        :class="{
          'flex-wrap': $vuetify.breakpoint.xs,
        }">
        <h3>Manage Users Invitation</h3>
        <v-btn @click="update()" small color="primary"
          ><v-icon left>mdi-plus</v-icon> Invite User</v-btn
        >

        <v-divider vertical class="mx-4"></v-divider>
        <v-btn-toggle v-model="activeUsers" mandatory color="primary" group>
          <v-btn
            :value="true"
            class="text-uppercase mr-0"
            small
            style="border-radius: 0.25rem"
            to="/users">
            <v-icon small v-if="activeUsers" left>mdi-check-circle</v-icon>
            <v-icon small v-else left>mdi-check-circle-outline</v-icon>

            Active Users
          </v-btn>
          <v-btn
            :value="false"
            class="text-uppercase"
            small
            style="border-radius: 0.25rem"
            to="/users-invitation"
            v-has="permissions.Users.Create">
            <v-icon small v-if="!activeUsers" left>mdi-share</v-icon>
            <v-icon small v-else left>mdi-share-outline</v-icon>

            Invited
          </v-btn>
        </v-btn-toggle>
        <v-spacer v-if="!$vuetify.breakpoint.xs"></v-spacer>
        <v-sheet :width="$vuetify.breakpoint.xs ? '100%' : 250">
          <v-text-field
            hide-details
            outlined
            v-model="options.search"
            dense
            class="search-bar"
            placeholder="Search in invited users..."
            clearable
            prepend-inner-icon="mdi-magnify"></v-text-field>
        </v-sheet>
      </div>
      <v-data-table
        class="elevation-0 pa-2 rounded-0 d-flex flex-column table-sticky custom-table"
        :footer-props="{
          showFirstLastPage: true,
          firstIcon: 'mdi-chevron-double-left',
          lastIcon: 'mdi-chevron-double-right',
          prevIcon: 'mdi-chevron-left',
          nextIcon: 'mdi-chevron-right',
          itemsPerPageText: 'Row/Page',
          itemsPerPageOptions: [15, 30, 50, 100, 200, 300, 400, 500, -1],
        }"
        :headers="userHeaders"
        :items="entries"
        :options.sync="options"
        :loading="loading"
        :server-items-length="total">
        <template v-slot:[`item.firstName`]="{ item }">
          <div class="d-inline-flex">
            <user-avatar :user="item" hide-roles disable-open-link></user-avatar>
          </div>
        </template>
        <template v-slot:[`item.email`]="{ item }">
          <a :href="'mailto:' + item.email">{{ item.email }}</a>
        </template>

        <template v-slot:[`item.roles`]="{ item }">
          <v-row style="margin: 5px 0px">
            <v-chip
              style="font-size: 11px; font-weight: 600"
              v-for="role in item.roles"
              :key="role.name"
              label
              x-small
              color="deep-purple"
              text-color="white"
              :ripple="false"
              link>
              {{ role.name }}
            </v-chip>
          </v-row>
        </template>
        <template v-slot:[`item.isActivated`]="{ item }">
          <span v-if="item.isActivated" class="green--text">
            <v-icon left>mdi-play</v-icon> Activated
          </span>
          <span v-else class="d-flex align-center opacity-40">
            <i class="fad fa-face-sleeping fs-18px mr-2"></i>Not Activated!
          </span>
        </template>

        <template v-slot:[`item.createDate`]="{ item }">
          {{ $formatDate(item.createDate) }}
        </template>
        <template v-slot:[`item.updateDate`]="{ item }">
          {{ $formatDate(item.updateDate) }}
        </template>

        <template v-slot:[`item.isExpired`]="{ item }">
          <v-chip
            small
            label
            :color="item.isExpired ? 'error' : 'success'"
            :text-color="'white'"
            class="font-weight-medium">
            <v-icon small left>{{ item.isExpired ? 'mdi-clock-alert-outline' : 'mdi-check-circle-outline' }}</v-icon>
            {{ item.isExpired ? 'Expired' : 'Valid' }}
          </v-chip>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-menu dense offset-x right>
            <template v-slot:activator="{ attrs, on }">
              <v-btn icon elevation="0" v-bind="attrs" v-on="on">
                <v-icon size="16px">mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list class="more-options-menu" dense>
              <v-list-item @click="resend(item.id)" dense>
                <v-list-item-icon class="mr-2 justify-center">
                  <v-icon small>mdi-pencil-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Resend Invitation</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item @click="del(item.id)" dense>
                <v-list-item-icon class="mr-2 justify-center">
                  <v-icon small color="error">mdi-delete-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Delete Invitation</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>

        <template v-slot:no-data>
          <div class="text-center d-flex flex-column">
            <img width="300" src="/img/notFound.svg" class="py-5 my-8" style="opacity: 0.54" />
            <p class="font-weight-medium">No Data Available!</p>
          </div>
        </template>

        <template v-slot:loading>
          <div class="text-center d-flex flex-column">
            <img width="300" src="/img/loading.svg" class="py-5 my-8" style="opacity: 0.54" />
            <p class="font-weight-medium">Loading Users...</p>
          </div>
        </template>
      </v-data-table>
      <EditUser ref="editUser"></EditUser>
    </v-sheet>
  </v-sheet>
</template>

<script>
  import UserAvatar from '@Share/components/UserAvatar.vue';
  import userHeaders from '@User/config/userInvitation.headers';
  import { mapGetters } from 'vuex';
  import EditUser from '@User/components/EditUser.vue';
  import permissions from '@Share/config/permissions';
  import { $formatDate } from '@Utils';

  export default {
    name: 'user-list',
    data() {
      return {
        userHeaders,
        activeUsers: false,
        permissions,
      };
    },
    computed: {
      ...mapGetters('USER_INVITATION', {
        loading: 'GET_LOADING',
        entries: 'GET_ENTRIES',
        total: 'GET_TOTAL',
      }),
      options: {
        get() {
          return this.$store.getters['USER_INVITATION/GET_OPTIONS'];
        },
        set(val) {
          this.$store.commit('USER_INVITATION/SET_OPTIONS', val);
        },
      },
    },
    methods: {
      $formatDate,
      update(userItemId = null) {
        this.$refs.editUser.open(userItemId, true);
      },
      resend(id) {
        this.$dialog.info({
          text: 'Are You Sure You Want to Resend This Invitation?',
          title: 'Resend Invitation',
          color: 'primary',
          actions: {
            false: 'Cancel',
            true: {
              text: 'Ok',
              color: 'primary',
              handle: () => {
                return this.$store.dispatch('USER_INVITATION/RESEND', id);
              },
            },
          },
        });
      },
      accept(id) {
        this.$store.dispatch('USER_INVITATION/ACCEPT', id);
      },
      del(id) {
        this.$dialog.error({
          text: 'Are You Sure You Want to Delete This Invitation?',
          title: 'Delete Invitation',
          actions: {
            false: 'Cancel',
            true: {
              text: 'Ok',
              color: 'primary',
              handle: () => {
                return this.$store.dispatch('USER_INVITATION/DELETE', id);
              },
            },
          },
        });
      },
    },
    watch: {
      options: {
        deep: true,
        handler() {
          this.$store.dispatch('USER_INVITATION/GET');
        },
      },
    },
    components: {
      UserAvatar,
      EditUser,
    },
  };
</script>
